import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Chip,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import DateInputWithArrows from './DateInputWithArrows';
import { formatDateEuropean } from '../utils/dateUtils';
import { formatPersonnelName } from '../utils/personnelDisplay';
import {
  getProjectDeadlineUpdates,
  createDeadlineUpdate,
  getProjectCostSheets,
  getProjectCostSheetDocuments,
  getProjectCostSheetDocumentContent,
  deleteProjectCostSheetDocument,
  getProjectTools,
  getProjectMaterials,
  getProjectPersonnel,
  getProjectLaborEntries,
  getProjectGeneralTasks,
  createProjectGeneralTask,
  deleteProjectGeneralTask,
  attachCostSheetsToProjectGeneralTask,
  detachCostSheetFromProjectGeneralTask,
  getGeneralTasks,
  getProjectFloorplans,
  createProjectFloorplanObject,
  updateProjectFloorplanObject,
  deleteProjectFloorplanObject,
  getProjectFloorplanUploadUrl,
  createProjectFloorplanFile,
  deleteProjectFloorplanFile,
  getProjectFloorplanFileContent,
} from '../services/api';
import { getApiErrorMessage } from '../utils/apiErrors';
import { compareCatalogTasks, uniqueCategoriesInSortOrder } from '../utils/generalTasks';
import CostSheetUpload from './CostSheetUpload';
import ProjectOtherDocumentsTab from './ProjectOtherDocumentsTab';
import { useTranslation } from 'react-i18next';

// TabPanel component for tab content
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`project-tabpanel-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function ProjectDetailsModal({ open, onClose, project, onProjectUpdate, initialTab = 0 }) {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(initialTab || 0);
  const [deadlineUpdates, setDeadlineUpdates] = useState([]);
  const [costSheets, setCostSheets] = useState([]);
  const [costSheetDocuments, setCostSheetDocuments] = useState([]);
  const [expandedCostSheetGroups, setExpandedCostSheetGroups] = useState({});
  const [tools, setTools] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [laborEntries, setLaborEntries] = useState([]);
  const [showDeadlineUpdateForm, setShowDeadlineUpdateForm] = useState(false);
  const [newDeadline, setNewDeadline] = useState({
    updatedEndDate: '',
    note: ''
  });
  const [generalTasksData, setGeneralTasksData] = useState({ tasks: [], coverage: null });
  const [catalogTasks, setCatalogTasks] = useState([]);
  const [addExistingDialogOpen, setAddExistingDialogOpen] = useState(false);
  const [createNewDialogOpen, setCreateNewDialogOpen] = useState(false);
  const [addExistingCategoryFilter, setAddExistingCategoryFilter] = useState('');
  const [selectedCatalogTask, setSelectedCatalogTask] = useState(null);
  const [expandedCostSheetTaskIds, setExpandedCostSheetTaskIds] = useState(() => new Set());
  const [newTaskCategory, setNewTaskCategory] = useState('');
  const [newGeneralTaskName, setNewGeneralTaskName] = useState('');
  const [newGeneralTaskDescription, setNewGeneralTaskDescription] = useState('');
  const [creatingTask, setCreatingTask] = useState(false);
  const [assigningTask, setAssigningTask] = useState(false);
  const [linkDialog, setLinkDialog] = useState({ open: false, taskId: null, selectedIds: [] });
  const [linkSearch, setLinkSearch] = useState('');
  const [linkSortDirection, setLinkSortDirection] = useState('asc');
  const [floorplanObjects, setFloorplanObjects] = useState([]);
  const [newFloorplanObject, setNewFloorplanObject] = useState({ objectName: '', floorCount: 1 });

  useEffect(() => {
    if (open && project) {
      setTabValue(initialTab || 0);
      fetchDeadlineUpdates();
      fetchProjectDetails();
    } else if (!open) {
      setTabValue(0);
    }
  }, [open, project, initialTab]);

  const fetchDeadlineUpdates = async () => {
    try {
      const response = await getProjectDeadlineUpdates(project.id);
      setDeadlineUpdates(response.data);
    } catch (error) {
      console.error('Error fetching deadline updates:', error);
    }
  };

  const fetchProjectDetails = async () => {
    try {
      const [
        costSheetsRes,
        costSheetDocumentsRes,
        toolsRes,
        materialsRes,
        personnelRes,
        laborEntriesRes,
        generalTasksRes,
        catalogTasksRes,
        floorplansRes,
      ] = await Promise.all([
        getProjectCostSheets(project.id),
        getProjectCostSheetDocuments(project.id),
        getProjectTools(project.id),
        getProjectMaterials(project.id),
        getProjectPersonnel(project.id),
        getProjectLaborEntries(project.id),
        getProjectGeneralTasks(project.id),
        getGeneralTasks(),
        getProjectFloorplans(project.id),
      ]);

      setCostSheets(costSheetsRes.data);
      setCostSheetDocuments(costSheetDocumentsRes.data);
      setTools(toolsRes.data);
      setMaterials(materialsRes.data);
      setPersonnel(personnelRes.data);
      setLaborEntries(laborEntriesRes.data);
      setGeneralTasksData({
        tasks: generalTasksRes.data?.tasks || [],
        coverage: generalTasksRes.data?.coverage || null,
      });
      setCatalogTasks(catalogTasksRes.data || []);
      setFloorplanObjects(floorplansRes.data || []);
    } catch (error) {
      console.error('Error fetching project details:', error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const refreshGeneralTasks = async () => {
    if (!project?.id) return;
    try {
      const [res, catalogRes] = await Promise.all([
        getProjectGeneralTasks(project.id),
        getGeneralTasks(),
      ]);
      setGeneralTasksData({
        tasks: res.data?.tasks || [],
        coverage: res.data?.coverage || null,
      });
      setCatalogTasks(catalogRes.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const refreshCostSheets = async () => {
    if (!project?.id) return;
    try {
      const [costSheetsRes, costSheetDocumentsRes] = await Promise.all([
        getProjectCostSheets(project.id),
        getProjectCostSheetDocuments(project.id),
      ]);
      setCostSheets(costSheetsRes.data);
      setCostSheetDocuments(costSheetDocumentsRes.data);
    } catch (error) {
      console.error('Error refreshing cost sheets:', error);
    }
  };

  const manualCostSheets = useMemo(
    () => (costSheets || []).filter((sheet) => !sheet.documentId && !sheet.document?.id),
    [costSheets]
  );

  const costSheetsByDocumentId = useMemo(() => {
    const map = new Map();
    (costSheets || []).forEach((sheet) => {
      const documentId = sheet.documentId ?? sheet.document?.id;
      if (!documentId) return;
      if (!map.has(documentId)) map.set(documentId, []);
      map.get(documentId).push(sheet);
    });
    return map;
  }, [costSheets]);

  const toggleCostSheetGroup = (groupKey) => {
    setExpandedCostSheetGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const handleDownloadCostSheetDocument = async (costSheetDocument) => {
    if (!project?.id || !costSheetDocument?.objectKey) return;
    try {
      const response = await getProjectCostSheetDocumentContent(project.id, costSheetDocument.id);
      const blobUrl = URL.createObjectURL(response.data);
      const link = window.document.createElement('a');
      link.href = blobUrl;
      link.download = costSheetDocument.name || 'cost-sheet.xlsx';
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      alert(await getApiErrorMessage(error, t('projectDetails.downloadDocumentFailed')));
    }
  };

  const handleDeleteCostSheetDocument = async (costSheetDocument) => {
    if (!project?.id || !costSheetDocument?.id) return;
    const count = costSheetDocument.costSheetCount ?? (costSheetsByDocumentId.get(costSheetDocument.id)?.length || 0);
    if (!window.confirm(t('projectDetails.deleteDocumentConfirm', { count }))) return;
    try {
      await deleteProjectCostSheetDocument(project.id, costSheetDocument.id);
      await refreshCostSheets();
      await refreshGeneralTasks();
    } catch (error) {
      const message = error.response?.status === 409
        ? t('projectDetails.deleteDocumentBlocked')
        : await getApiErrorMessage(error, error.message);
      alert(message);
    }
  };

  const sortCostSheets = (sheets) => [...(sheets || [])].sort((a, b) => {
    const aOrder = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
    const bOrder = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return a.id - b.id;
  });

  const renderCostSheetTable = (sheets) => (
    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('projects.fields.name')}</TableCell>
            <TableCell>{t('projectDetails.itemType')}</TableCell>
            <TableCell>{t('projects.fields.workType')}</TableCell>
            <TableCell>{t('costSheetsPage.quantity')}</TableCell>
            <TableCell>{t('costSheetsPage.unitPrice')}</TableCell>
            <TableCell>{t('costSheetsPage.totalPrice')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sheets.length > 0 ? (
            sortCostSheets(sheets).map((sheet) => (
              <TableRow key={sheet.id}>
                <TableCell>{sheet.name}</TableCell>
                <TableCell>{sheet.itemType}</TableCell>
                <TableCell>{sheet.workType}</TableCell>
                <TableCell>{sheet.quantity} {sheet.unit}</TableCell>
                <TableCell>{new Intl.NumberFormat('hr-HR', { style: 'currency', currency: 'EUR' }).format(sheet.unitPrice)}</TableCell>
                <TableCell>{new Intl.NumberFormat('hr-HR', { style: 'currency', currency: 'EUR' }).format(sheet.totalPrice)}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography variant="body2" color="text.secondary">
                  {t('projectDetails.noCostSheets', { defaultValue: 'No cost sheets found. Upload an Excel file to get started.' })}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const assignedCostSheetIds = new Set(
    (generalTasksData.tasks || []).flatMap((t) => (t.costSheetLinks || []).map((l) => l.costSheetId))
  );

  const unassignedCostSheets = (costSheets || []).filter((cs) => !assignedCostSheetIds.has(cs.id));
  const filteredUnassignedCostSheets = useMemo(() => {
    const query = linkSearch.trim().toLowerCase();
    const sorted = [...unassignedCostSheets].sort((a, b) => {
      const codeA = String(a.code || '').toLowerCase();
      const codeB = String(b.code || '').toLowerCase();
      if (codeA !== codeB) {
        return linkSortDirection === 'asc'
          ? codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' })
          : codeB.localeCompare(codeA, undefined, { numeric: true, sensitivity: 'base' });
      }
      const nameA = String(a.name || '').toLowerCase();
      const nameB = String(b.name || '').toLowerCase();
      return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
    });
    if (!query) return sorted;
    return sorted.filter((cs) =>
      [cs.code, cs.name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [unassignedCostSheets, linkSearch, linkSortDirection]);

  const allFilteredSelected =
    filteredUnassignedCostSheets.length > 0 &&
    filteredUnassignedCostSheets.every((cs) => linkDialog.selectedIds.includes(cs.id));
  const someFilteredSelected =
    filteredUnassignedCostSheets.some((cs) => linkDialog.selectedIds.includes(cs.id)) && !allFilteredSelected;

  const availableCatalogTasks = useMemo(() => {
    const assignedIds = new Set(
      (generalTasksData.tasks || [])
        .map((t) => t.generalTaskId)
        .filter((id) => Number.isFinite(id))
    );
    return (catalogTasks || [])
      .filter((t) => !assignedIds.has(t.id))
      .slice()
      .sort(compareCatalogTasks);
  }, [catalogTasks, generalTasksData.tasks]);

  const availableCatalogCategories = useMemo(
    () => uniqueCategoriesInSortOrder(availableCatalogTasks),
    [availableCatalogTasks]
  );

  const filteredAvailableCatalogTasks = useMemo(() => {
    if (!addExistingCategoryFilter) return availableCatalogTasks;
    return availableCatalogTasks.filter(
      (task) => (task.category || '').toLowerCase() === addExistingCategoryFilter.toLowerCase()
    );
  }, [availableCatalogTasks, addExistingCategoryFilter]);

  const allCategoryOptions = useMemo(
    () => uniqueCategoriesInSortOrder([...(catalogTasks || []), ...(generalTasksData.tasks || [])]),
    [catalogTasks, generalTasksData.tasks]
  );

  const openAddExistingDialog = () => {
    setAddExistingCategoryFilter('');
    setSelectedCatalogTask(null);
    setAddExistingDialogOpen(true);
  };

  const closeAddExistingDialog = () => {
    setAddExistingDialogOpen(false);
    setAddExistingCategoryFilter('');
    setSelectedCatalogTask(null);
  };

  const toggleCostSheetsExpanded = (taskId) => {
    setExpandedCostSheetTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const openCreateNewDialog = () => {
    setNewTaskCategory('');
    setNewGeneralTaskName('');
    setNewGeneralTaskDescription('');
    setCreateNewDialogOpen(true);
  };

  const closeCreateNewDialog = () => {
    setCreateNewDialogOpen(false);
    setNewTaskCategory('');
    setNewGeneralTaskName('');
    setNewGeneralTaskDescription('');
  };

  const handleAssignCatalogTask = async () => {
    if (!selectedCatalogTask?.id || !project?.id) return;
    try {
      setAssigningTask(true);
      await createProjectGeneralTask(project.id, { generalTaskId: selectedCatalogTask.id });
      closeAddExistingDialog();
      await refreshGeneralTasks();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setAssigningTask(false);
    }
  };

  const handleCreateGeneralTask = async () => {
    const name = newGeneralTaskName.trim();
    if (!name || !project?.id) return;
    try {
      setCreatingTask(true);
      await createProjectGeneralTask(project.id, {
        name,
        category: (newTaskCategory || '').trim(),
        description: newGeneralTaskDescription.trim(),
      });
      closeCreateNewDialog();
      await refreshGeneralTasks();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setCreatingTask(false);
    }
  };

  const handleDeleteGeneralTask = async (taskId) => {
    if (!project?.id || !window.confirm(t('projectDetails.deleteTaskConfirm'))) return;
    try {
      await deleteProjectGeneralTask(project.id, taskId);
      await refreshGeneralTasks();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    }
  };

  const openLinkDialog = (taskId) => {
    setLinkSearch('');
    setLinkSortDirection('asc');
    setLinkDialog({ open: true, taskId, selectedIds: [] });
  };

  const toggleLinkSheet = (costSheetId) => {
    setLinkDialog((d) => {
      const set = new Set(d.selectedIds);
      if (set.has(costSheetId)) set.delete(costSheetId);
      else set.add(costSheetId);
      return { ...d, selectedIds: [...set] };
    });
  };

  const applyLinkSheets = async () => {
    if (!project?.id || !linkDialog.taskId || !linkDialog.selectedIds.length) return;
    try {
      await attachCostSheetsToProjectGeneralTask(project.id, linkDialog.taskId, linkDialog.selectedIds);
      setLinkDialog({ open: false, taskId: null, selectedIds: [] });
      await refreshGeneralTasks();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    }
  };

  const toggleSelectAllFiltered = () => {
    setLinkDialog((d) => {
      const selectedSet = new Set(d.selectedIds);
      if (allFilteredSelected) {
        filteredUnassignedCostSheets.forEach((cs) => selectedSet.delete(cs.id));
      } else {
        filteredUnassignedCostSheets.forEach((cs) => selectedSet.add(cs.id));
      }
      return { ...d, selectedIds: [...selectedSet] };
    });
  };

  const handleDetachSheet = async (taskId, costSheetId) => {
    if (!project?.id) return;
    try {
      await detachCostSheetFromProjectGeneralTask(project.id, taskId, costSheetId);
      await refreshGeneralTasks();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    }
  };

  const refreshFloorplans = async () => {
    if (!project?.id) return;
    try {
      const response = await getProjectFloorplans(project.id);
      setFloorplanObjects(response.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleCreateFloorplanObject = async () => {
    const objectName = newFloorplanObject.objectName.trim();
    if (!objectName) return;
    try {
      await createProjectFloorplanObject(project.id, {
        objectName,
        floorCount: parseInt(newFloorplanObject.floorCount, 10) || 1,
        floorNames: {},
      });
      setNewFloorplanObject({ objectName: '', floorCount: 1 });
      await refreshFloorplans();
    } catch (error) {
      alert(error.response?.data?.error || error.message);
    }
  };

  const handleSaveFloorplanObject = async (objectRow) => {
    try {
      await updateProjectFloorplanObject(project.id, objectRow.id, {
        objectName: objectRow.objectName,
        floorCount: parseInt(objectRow.floorCount, 10) || 1,
        floorNames: objectRow.floorNames || {},
      });
      await refreshFloorplans();
    } catch (error) {
      alert(error.response?.data?.error || error.message);
    }
  };

  const handleDeleteFloorplanObject = async (objectId) => {
    try {
      await deleteProjectFloorplanObject(project.id, objectId);
      await refreshFloorplans();
    } catch (error) {
      alert(error.response?.data?.error || error.message);
    }
  };

  const handleUploadFloorplanFile = async (objectId, floorNumber, file) => {
    if (!file) return;
    try {
      const presign = await getProjectFloorplanUploadUrl(project.id, objectId, {
        floorNumber,
        originalName: file.name,
        contentType: file.type || 'application/octet-stream',
      });
      const { uploadUrl, objectKey } = presign.data;
      await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      await createProjectFloorplanFile(project.id, objectId, {
        floorNumber,
        objectKey,
        originalName: file.name,
        mimeType: file.type || 'application/octet-stream',
        size: file.size || 0,
      });
      await refreshFloorplans();
    } catch (error) {
      alert(error.response?.data?.error || error.message);
    }
  };

  const handleDeleteFloorplanFile = async (fileId) => {
    try {
      await deleteProjectFloorplanFile(project.id, fileId);
      await refreshFloorplans();
    } catch (error) {
      alert(error.response?.data?.error || error.message);
    }
  };

  const handlePreviewFloorplanFile = async (fileId) => {
    try {
      const res = await getProjectFloorplanFileContent(project.id, fileId);
      const objectUrl = URL.createObjectURL(res.data);
      const previewWindow = window.open(objectUrl, '_blank', 'noopener,noreferrer');
      if (!previewWindow) {
        URL.revokeObjectURL(objectUrl);
        alert('Pop-up blocked. Allow pop-ups to preview the floorplan.');
        return;
      }
      previewWindow.addEventListener('beforeunload', () => URL.revokeObjectURL(objectUrl));
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (error) {
      alert(await getApiErrorMessage(error, 'Failed to preview floorplan'));
    }
  };

  const calculateProjectStatus = () => {
    if (!project) return null;

    const today = new Date();
    const endDate = new Date(project.endDate);
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return <Chip color="error" label={`${Math.abs(diffDays)} days late`} />;
    } else {
      return <Chip color="success" label={`${diffDays} days remaining`} />;
    }
  };

  const handleDeadlineUpdateSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side validation: check if the selected date is before today
    const selectedDate = new Date(newDeadline.updatedEndDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      alert('New deadline date cannot be before today\'s date');
      return;
    }
    
    try {
      await createDeadlineUpdate({
        projectId: project.id,
        ...newDeadline
      });
      setShowDeadlineUpdateForm(false);
      setNewDeadline({ updatedEndDate: '', note: '' });
      fetchDeadlineUpdates();
      
      // Notify parent component to refresh project data
      if (onProjectUpdate) {
        onProjectUpdate();
      }
    } catch (error) {
      console.error('Error updating deadline:', error);
      // Show error message to user
      if (error.response?.data?.error) {
        alert(error.response.data.error);
      } else {
        alert('Error updating deadline. Please try again.');
      }
    }
  };

  // Update Resources Tab content
  const ResourcesTab = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t('warehouse.sections.tools')}</Typography>
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('projectDetails.toolName')}</TableCell>
              <TableCell>{t('tools.fields.serialNumber')}</TableCell>
              <TableCell>{t('projectDetails.assignedTo')}</TableCell>
              <TableCell>{t('tools.fields.dateReceived')}</TableCell>
              <TableCell>{t('tools.fields.dateReturned')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tools.map((tool) => (
              <TableRow key={tool.id}>
                <TableCell>{tool.name}</TableCell>
                <TableCell>{tool.serialNumber}</TableCell>
                <TableCell>
                  {formatPersonnelName(tool.personnel[0]?.personnel)}
                </TableCell>
                <TableCell>{formatDateEuropean(tool.dateReceived)}</TableCell>
                <TableCell>
                  {tool.dateReturned ? formatDateEuropean(tool.dateReturned) : '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" sx={{ mb: 2 }}>{t('warehouse.sections.materials')}</Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('projectDetails.materialType')}</TableCell>
              <TableCell>{t('common.description')}</TableCell>
              <TableCell>{t('costSheetsPage.quantity')}</TableCell>
              <TableCell>{t('costSheetsPage.unitPrice')}</TableCell>
              <TableCell>{t('projectDetails.deliveryDate')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {materials.map((material) => (
              <TableRow key={material.id}>
                <TableCell>{material.materialType}</TableCell>
                <TableCell>{material.description}</TableCell>
                <TableCell>{material.quantity} {material.unit}</TableCell>
                <TableCell>
                  {new Intl.NumberFormat('hr-HR', { style: 'currency', currency: 'EUR' }).format(material.unitPrice)}
                </TableCell>
                <TableCell>{formatDateEuropean(material.deliveryDate)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  // Update Personnel Tab content
  const PersonnelTab = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t('projectDetails.projectPersonnel')}</Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('projects.fields.name')}</TableCell>
              <TableCell>{t('personnel.fields.company')}</TableCell>
              <TableCell>{t('personnel.fields.role')}</TableCell>
              <TableCell>{t('projectDetails.totalHours')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {personnel.map((person) => {
              const totalHours = person.laborEntries.reduce((sum, entry) => sum + entry.hoursWorked, 0);
              return (
                <TableRow key={person.id}>
                  <TableCell>{formatPersonnelName(person)}</TableCell>
                  <TableCell>{person.company.name}</TableCell>
                  <TableCell>{person.role}</TableCell>
                  <TableCell>{totalHours.toFixed(2)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  if (!project) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{project.name}</Typography>
          {calculateProjectStatus()}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab label={t('projectDetails.tabs.overview', { defaultValue: 'Overview' })} />
            <Tab label={t('projectDetails.tabs.deadlineUpdates', { defaultValue: 'Deadline Updates' })} />
            <Tab label={t('projectDetails.tabs.costSheets', { defaultValue: 'Cost Sheets' })} />
            <Tab label={t('projectDetails.tabs.generalTasks', { defaultValue: 'General Tasks' })} />
            <Tab label={t('projectDetails.tabs.resources', { defaultValue: 'Resources' })} />
            <Tab label={t('projectDetails.tabs.personnel', { defaultValue: 'Personnel' })} />
            <Tab label={t('projectDetails.tabs.floorplans', { defaultValue: 'Floorplans' })} />
            <Tab label={t('projectDetails.tabs.otherDocuments', { defaultValue: 'Other documents' })} />
          </Tabs>
        </Box>

        {/* Overview Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box>
            <Typography variant="subtitle1">{t('projectDetails.projectDetails', { defaultValue: 'Project Details' })}</Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell><strong>{t('projects.fields.investor')}</strong></TableCell>
                    <TableCell>{project.investor}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>{t('projects.fields.location')}</strong></TableCell>
                    <TableCell>{project.location}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>{t('projects.fields.supervisors')}</strong></TableCell>
                    <TableCell>
                      {project.supervisors?.length > 0
                        ? project.supervisors.map((s) => {
                            const parts = [s.supervisorName, s.supervisingField, s.supervisorCompany].filter(Boolean);
                            return parts.join(' — ');
                          }).join(', ')
                        : (project.supervisor || t('projects.noSupervisors'))}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>{t('projects.fields.startDate')}</strong></TableCell>
                    <TableCell>{formatDateEuropean(project.startDate)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>{t('projects.fields.endDate')}</strong></TableCell>
                    <TableCell>{project.endDate ? formatDateEuropean(project.endDate) : t('common.notSet')}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>{t('projects.fields.status')}</strong></TableCell>
                    <TableCell>{project.status}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>{t('projects.fields.agreedPrice')}</strong></TableCell>
                    <TableCell>
                      {new Intl.NumberFormat('hr-HR', { style: 'currency', currency: 'EUR' }).format(project.agreedPrice)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>

        {/* Deadline Updates Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowDeadlineUpdateForm(true)}
            >
              {t('projectDetails.updateDeadline', { defaultValue: 'Update Deadline' })}
            </Button>
          </Box>

          {showDeadlineUpdateForm && (
            <Box component="form" onSubmit={handleDeadlineUpdateSubmit} sx={{ mb: 3 }}>
              <DateInputWithArrows
                fullWidth
                margin="normal"
                label={t('projectDetails.newEndDate', { defaultValue: 'New End Date' })}
                value={newDeadline.updatedEndDate}
                onChange={(e) => setNewDeadline({ ...newDeadline, updatedEndDate: e.target.value })}
                inputProps={{
                  min: new Date().toISOString().split('T')[0] // Set minimum date to today
                }}
                required
              />
              <TextField
                fullWidth
                margin="normal"
                label={t('projectDetails.note')}
                value={newDeadline.note}
                onChange={(e) => setNewDeadline({ ...newDeadline, note: e.target.value })}
                multiline
                rows={2}
                required
              />
              <Button type="submit" variant="contained" color="primary">
                {t('projectDetails.saveUpdate')}
              </Button>
            </Box>
          )}

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('projectDetails.updatedDate')}</TableCell>
                  <TableCell>{t('projectDetails.previousDate')}</TableCell>
                  <TableCell>{t('projectDetails.note')}</TableCell>
                  <TableCell>{t('projectDetails.updatedAt')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deadlineUpdates.map((update, index) => (
                  <TableRow key={update.id}>
                    <TableCell>{formatDateEuropean(update.updatedEndDate)}</TableCell>
                    <TableCell>
                      {update.previousEndDate 
                        ? formatDateEuropean(update.previousEndDate)
                        : t('projectDetails.noPreviousDate', { defaultValue: 'No previous date' })}
                    </TableCell>
                    <TableCell>{update.note}</TableCell>
                    <TableCell>{new Date(update.createdAt).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Cost Sheets Tab */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            {t('projectDetails.tabs.costSheets', { defaultValue: 'Cost Sheets' })}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <CostSheetUpload
                projectId={project.id}
                onUploadSuccess={refreshCostSheets}
              />
            </Grid>
          </Grid>

          {costSheetDocuments.map((doc) => {
            const groupKey = `doc-${doc.id}`;
            const sheets = costSheetsByDocumentId.get(doc.id) || [];
            const expanded = !!expandedCostSheetGroups[groupKey];
            return (
              <Paper key={doc.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Typography variant="subtitle2" sx={{ flex: 1 }}>
                    {doc.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('projectDetails.uploadedAt')}: {formatDateEuropean(doc.uploadedAt)}
                  </Typography>
                  <Chip
                    size="small"
                    label={t('projectDetails.itemCount', { count: doc.costSheetCount ?? sheets.length })}
                  />
                  {doc.objectKey && (
                    <Button size="small" variant="outlined" onClick={() => handleDownloadCostSheetDocument(doc)}>
                      {t('projectDetails.downloadDocument')}
                    </Button>
                  )}
                  <Button size="small" color="error" onClick={() => handleDeleteCostSheetDocument(doc)}>
                    {t('projectDetails.deleteDocument')}
                  </Button>
                  <Button size="small" onClick={() => toggleCostSheetGroup(groupKey)}>
                    {expanded ? t('projectDetails.collapse') : t('projectDetails.expand')}
                  </Button>
                </Box>
                {expanded && renderCostSheetTable(sheets)}
              </Paper>
            );
          })}

          {manualCostSheets.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="subtitle2" sx={{ flex: 1 }}>
                  {t('projectDetails.manualEntries')}
                </Typography>
                <Chip
                  size="small"
                  label={t('projectDetails.itemCount', { count: manualCostSheets.length })}
                />
                <Button size="small" onClick={() => toggleCostSheetGroup('manual')}>
                  {expandedCostSheetGroups.manual ? t('projectDetails.collapse') : t('projectDetails.expand')}
                </Button>
              </Box>
              {expandedCostSheetGroups.manual && renderCostSheetTable(manualCostSheets)}
            </Paper>
          )}

          {costSheetDocuments.length === 0 && manualCostSheets.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {t('projectDetails.noCostSheets', { defaultValue: 'No cost sheets found. Upload an Excel file to get started.' })}
            </Typography>
          )}
        </TabPanel>

        {/* General Tasks Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('projectDetails.costSheetCoverage')}
            </Typography>
            {generalTasksData.coverage && generalTasksData.coverage.totalCostSheets > 0 ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={
                        (100 * generalTasksData.coverage.assignedCostSheets) /
                        generalTasksData.coverage.totalCostSheets
                      }
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {generalTasksData.coverage.assignedCostSheets} / {generalTasksData.coverage.totalCostSheets}{' '}
                    ({Math.round(
                      (100 * generalTasksData.coverage.assignedCostSheets) /
                        generalTasksData.coverage.totalCostSheets
                    )}
                    %)
                  </Typography>
                </Box>
                {generalTasksData.coverage.unassignedCount > 0 && (
                  <Typography variant="caption" color="warning.main">
                    {t('projectDetails.unassignedCostSheets', {
                      count: generalTasksData.coverage.unassignedCount,
                    })}
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('projectDetails.noCostSheetsYet')}
              </Typography>
            )}
          </Box>

          <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={openAddExistingDialog}
              disabled={availableCatalogTasks.length === 0}
            >
              {t('projectDetails.addExistingGeneralTask')}
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateNewDialog}>
              {t('projectDetails.createNewGeneralTask')}
            </Button>
          </Box>

          {(generalTasksData.tasks || []).map((task) => (
            <Paper key={task.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
                <Box flex={1} minWidth={200}>
                  <Typography variant="subtitle1">{task.name}</Typography>
                  {(task.category || task.isMaster) && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {[
                        task.isMaster ? t('generalTasks.sourceSystem') : null,
                        task.category || null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                  )}
                  {task.description ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {task.description}
                    </Typography>
                  ) : null}
                </Box>
                <Box display="flex" gap={0.5}>
                  <Button size="small" variant="outlined" onClick={() => openLinkDialog(task.id)}>
                    {t('projectDetails.linkCostSheets', { defaultValue: 'Link cost sheets' })}
                  </Button>
                  <Button size="small" color="error" onClick={() => handleDeleteGeneralTask(task.id)}>
                    {t('common.delete')}
                  </Button>
                </Box>
              </Box>
              <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1 }} flexWrap="wrap">
                <Typography variant="caption" color="text.secondary">
                  {t('projectDetails.linkedPositions', { count: (task.costSheetLinks || []).length })}
                </Typography>
                {(task.costSheetLinks || []).length > 0 && (
                  <Button size="small" onClick={() => toggleCostSheetsExpanded(task.id)}>
                    {expandedCostSheetTaskIds.has(task.id)
                      ? t('projectDetails.hideCostSheets')
                      : t('projectDetails.showCostSheets')}
                  </Button>
                )}
              </Box>
              {expandedCostSheetTaskIds.has(task.id) && (task.costSheetLinks || []).length > 0 && (
                <List dense disablePadding sx={{ mt: 1 }}>
                  {(task.costSheetLinks || []).map((link) => (
                    <ListItem
                      key={link.id}
                      secondaryAction={
                        <Button size="small" onClick={() => handleDetachSheet(task.id, link.costSheetId)}>
                          {t('common.remove')}
                        </Button>
                      }
                    >
                      <ListItemText
                        primary={
                          link.costSheet?.code
                            ? `${link.costSheet.code} – ${link.costSheet.name}`
                            : link.costSheet?.name || `Sheet #${link.costSheetId}`
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          ))}

          {(!generalTasksData.tasks || generalTasksData.tasks.length === 0) && (
            <Typography variant="body2" color="text.secondary">
              {t('projectDetails.noGeneralTasks')}
            </Typography>
          )}

          <Dialog
            open={linkDialog.open}
            onClose={() => {
              setLinkSearch('');
              setLinkSortDirection('asc');
              setLinkDialog({ open: false, taskId: null, selectedIds: [] });
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>{t('projectDetails.linkCostSheets', { defaultValue: 'Link cost sheets' })}</DialogTitle>
            <DialogContent>
              {unassignedCostSheets.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('projectDetails.allCostSheetsAssigned')}
                </Typography>
              ) : (
                <>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5, mt: 0.5 }}>
                    <TextField
                      size="small"
                      fullWidth
                      label={t('projectDetails.searchCostSheets')}
                      placeholder={t('projectDetails.searchByCodeOrName')}
                      value={linkSearch}
                      onChange={(e) => setLinkSearch(e.target.value)}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        setLinkSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
                      }
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {linkSortDirection === 'asc'
                        ? t('projectDetails.sortCodeAsc')
                        : t('projectDetails.sortCodeDesc')}
                    </Button>
                  </Box>
                  <FormControlLabel
                    sx={{ mb: 1 }}
                    control={
                      <Checkbox
                        checked={allFilteredSelected}
                        indeterminate={someFilteredSelected}
                        onChange={toggleSelectAllFiltered}
                        disabled={filteredUnassignedCostSheets.length === 0}
                      />
                    }
                    label={t('projectDetails.selectAllInList', {
                      count: filteredUnassignedCostSheets.length,
                    })}
                  />
                  <List dense>
                    {filteredUnassignedCostSheets.map((cs) => (
                      <ListItem key={cs.id} disablePadding>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={linkDialog.selectedIds.includes(cs.id)}
                              onChange={() => toggleLinkSheet(cs.id)}
                            />
                          }
                          label={cs.code ? `${cs.code} – ${cs.name}` : cs.name}
                        />
                      </ListItem>
                    ))}
                    {filteredUnassignedCostSheets.length === 0 && (
                      <ListItem disablePadding>
                        <Typography variant="body2" color="text.secondary">
                          {t('projectDetails.noCostSheetsMatch')}
                        </Typography>
                      </ListItem>
                    )}
                  </List>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setLinkSearch('');
                  setLinkSortDirection('asc');
                  setLinkDialog({ open: false, taskId: null, selectedIds: [] });
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="contained"
                onClick={applyLinkSheets}
                disabled={!linkDialog.selectedIds.length}
              >
                {t('projectDetails.linkSelected', { defaultValue: 'Link selected' })}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={addExistingDialogOpen} onClose={closeAddExistingDialog} maxWidth="sm" fullWidth>
            <DialogTitle>{t('projectDetails.addExistingGeneralTask')}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                <InputLabel id="add-existing-category-filter">{t('generalTasks.fields.category')}</InputLabel>
                <Select
                  labelId="add-existing-category-filter"
                  label={t('generalTasks.fields.category')}
                  value={addExistingCategoryFilter}
                  onChange={(e) => {
                    setAddExistingCategoryFilter(e.target.value);
                    setSelectedCatalogTask(null);
                  }}
                >
                  <MenuItem value="">{t('generalTasks.allCategories')}</MenuItem>
                  {availableCatalogCategories.map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Autocomplete
                size="small"
                options={filteredAvailableCatalogTasks}
                groupBy={(option) => {
                  const category = option?.category || t('generalTasks.allCategories');
                  return option?.isMaster || option?.organisationId == null
                    ? `${category} · ${t('generalTasks.sourceSystem')}`
                    : `${category} · ${t('generalTasks.sourceCreated')}`;
                }}
                getOptionLabel={(option) => option?.name || ''}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2">{option.name}</Typography>
                      {option.description ? (
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      ) : null}
                    </Box>
                  </li>
                )}
                value={selectedCatalogTask}
                onChange={(_event, value) => setSelectedCatalogTask(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('projectDetails.selectExistingTask')}
                    placeholder={
                      filteredAvailableCatalogTasks.length === 0
                        ? t('projectDetails.noCatalogTasksAvailable')
                        : undefined
                    }
                  />
                )}
                noOptionsText={t('projectDetails.noCatalogTasksAvailable')}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={closeAddExistingDialog}>{t('common.cancel')}</Button>
              <Button
                variant="contained"
                onClick={handleAssignCatalogTask}
                disabled={!selectedCatalogTask || assigningTask}
              >
                {t('projectDetails.addSelectedTask')}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={createNewDialogOpen} onClose={closeCreateNewDialog} maxWidth="sm" fullWidth>
            <DialogTitle>{t('projectDetails.createNewGeneralTask')}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Autocomplete
                freeSolo
                size="small"
                options={allCategoryOptions}
                value={newTaskCategory}
                onChange={(_event, value) => setNewTaskCategory(value || '')}
                onInputChange={(_event, value) => setNewTaskCategory(value || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('generalTasks.fields.category')}
                    helperText={t('projectDetails.categoryFreeSoloHint')}
                    sx={{ mt: 1 }}
                  />
                )}
              />
              <TextField
                size="small"
                fullWidth
                required
                label={t('generalTasks.fields.name')}
                value={newGeneralTaskName}
                onChange={(e) => setNewGeneralTaskName(e.target.value)}
              />
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={2}
                label={t('generalTasks.fields.description')}
                value={newGeneralTaskDescription}
                onChange={(e) => setNewGeneralTaskDescription(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={closeCreateNewDialog}>{t('common.cancel')}</Button>
              <Button
                variant="contained"
                onClick={handleCreateGeneralTask}
                disabled={!newGeneralTaskName.trim() || creatingTask}
              >
                {t('projectDetails.createAndAssign')}
              </Button>
            </DialogActions>
          </Dialog>
        </TabPanel>

        {/* Resources Tab */}
        <TabPanel value={tabValue} index={4}>
          <ResourcesTab />
        </TabPanel>

        {/* Personnel Tab */}
        <TabPanel value={tabValue} index={5}>
          <PersonnelTab />
        </TabPanel>

        <TabPanel value={tabValue} index={6}>
          <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              label={t('projectDetails.objectName')}
              value={newFloorplanObject.objectName}
              onChange={(e) => setNewFloorplanObject((prev) => ({ ...prev, objectName: e.target.value }))}
            />
            <TextField
              size="small"
              label={t('projectDetails.floors')}
              type="number"
              value={newFloorplanObject.floorCount}
              onChange={(e) => setNewFloorplanObject((prev) => ({ ...prev, floorCount: e.target.value }))}
              inputProps={{ min: 1, step: 1 }}
              sx={{ width: 120 }}
            />
            <Button variant="contained" onClick={handleCreateFloorplanObject}>{t('projectDetails.addObject')}</Button>
          </Box>
          {floorplanObjects.map((objectRow) => (
            <Paper key={objectRow.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
                <TextField
                  size="small"
                  label={t('projectDetails.objectName')}
                  value={objectRow.objectName}
                  onChange={(e) => {
                    const objectName = e.target.value;
                    setFloorplanObjects((prev) => prev.map((item) => (
                      item.id === objectRow.id ? { ...item, objectName } : item
                    )));
                  }}
                />
                <TextField
                  size="small"
                  label={t('projectDetails.floors')}
                  type="number"
                  value={objectRow.floorCount}
                  onChange={(e) => {
                    const floorCount = e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value, 10) || 1);
                    setFloorplanObjects((prev) => prev.map((item) => (
                      item.id === objectRow.id ? { ...item, floorCount } : item
                    )));
                  }}
                  inputProps={{ min: 1, step: 1 }}
                  sx={{ width: 120 }}
                />
                <Button size="small" onClick={() => handleSaveFloorplanObject(objectRow)}>{t('common.save')}</Button>
                <Button size="small" color="error" onClick={() => handleDeleteFloorplanObject(objectRow.id)}>{t('common.delete')}</Button>
                <Button size="small" variant="outlined" component="label">
                  {t('common.upload')}
                  <input
                    hidden
                    type="file"
                    accept=".pdf,image/*"
                    onChange={(e) => {
                      const selectedFile = e.target.files?.[0];
                      if (selectedFile) handleUploadFloorplanFile(objectRow.id, selectedFile);
                      e.target.value = '';
                    }}
                  />
                </Button>
              </Box>
              <List dense>
                {Array.from({ length: Math.max(1, parseInt(objectRow.floorCount, 10) || 1) }, (_, idx) => {
                  const floorNumber = idx + 1;
                  const floorName = objectRow.floorNames?.[String(floorNumber)] || '';
                  const floorFile = (objectRow.files || []).find((item) => item.floorNumber === floorNumber);
                  return (
                    <ListItem
                      key={`${objectRow.id}-floor-${floorNumber}`}
                      secondaryAction={(
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button size="small" variant="outlined" component="label">
                            {floorFile ? t('common.replace') : t('common.upload')}
                            <input
                              hidden
                              type="file"
                              accept=".pdf,image/*"
                              onChange={(e) => {
                                const selectedFile = e.target.files?.[0];
                                if (selectedFile) handleUploadFloorplanFile(objectRow.id, floorNumber, selectedFile);
                                e.target.value = '';
                              }}
                            />
                          </Button>
                          {floorFile && (
                            <>
                              <Button size="small" onClick={() => handlePreviewFloorplanFile(floorFile.id)}>{t('common.preview')}</Button>
                              <Button size="small" color="error" onClick={() => handleDeleteFloorplanFile(floorFile.id)}>{t('common.delete')}</Button>
                            </>
                          )}
                        </Box>
                      )}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ minWidth: 80 }}>
                              {t('projectDetails.floorNumber', { number: floorNumber })}
                            </Typography>
                            <TextField
                              size="small"
                              placeholder={t('projectDetails.floorNamePlaceholder')}
                              value={floorName}
                              onChange={(e) => {
                                const value = e.target.value;
                                setFloorplanObjects((prev) => prev.map((item) => {
                                  if (item.id !== objectRow.id) return item;
                                  return {
                                    ...item,
                                    floorNames: {
                                      ...(item.floorNames || {}),
                                      [String(floorNumber)]: value,
                                    },
                                  };
                                }));
                              }}
                              sx={{ minWidth: 220 }}
                            />
                          </Box>
                        }
                        secondary={
                          floorFile
                            ? `${floorFile.originalName} - ${new Date(floorFile.uploadedAt).toLocaleString()}`
                            : t('projectDetails.noFileUploaded')
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Paper>
          ))}
        </TabPanel>

        <TabPanel value={tabValue} index={7}>
          <ProjectOtherDocumentsTab project={project} />
        </TabPanel>

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default ProjectDetailsModal; 